import { memo, useMemo } from "react";

import dayjs from "dayjs";
import type Highcharts from "highcharts";
import ReactDOMServer from "react-dom/server";

import { ReactHighcharts } from "app/libraries/reactHighcharts";
import { swSettings } from "app/common/services/swSettings";
import { FORMATTERS } from "app/common/benchmark/formatters";

import { DEFAULT_DECIMAL, DEFAULT_LINE_COLORS, LINE_WIDTH } from "./configs";
import * as SC from "./styles";
import type { LineChartProps, TooltipFormatterContext } from "./types";
import { deepMerge } from "./utils";

import "dayjs/locale/fr";
import "dayjs/locale/ja";
import "dayjs/locale/zh";
import "dayjs/locale/ko";

export const LineChart = memo(function LineChart({
    data,
    colorSchema,
    height,
    title,
    tooltipEnabled = true,
    tooltipFormatter,
    tooltipOptions,
    gradient,
    configOverride,
    hasMarkers = false,
    hasHover = false,
    lineWidth = LINE_WIDTH,
}: LineChartProps) {
    const colors = useMemo(() => ({ ...DEFAULT_LINE_COLORS, ...(colorSchema ?? {}) }), [colorSchema]);
    const safeData = data ?? [];

    const categories = useMemo(() => safeData.map((d) => d.key), [safeData]);
    const seriesData = useMemo(() => safeData.map((d) => d.value), [safeData]);

    const yExtremes = useMemo(() => {
        const nums = seriesData.filter((v): v is number => typeof v === "number" && Number.isFinite(v));
        if (!nums.length) {
            return null;
        }

        let min = Math.min(...nums);
        let max = Math.max(...nums);

        if (min === max) {
            const delta = Math.abs(max) > 0 ? Math.abs(max) * 0.1 : 1;
            min -= delta;
            max += delta;
        }

        const range = max - min;
        const bottomPad = range * 0.05;
        const topPad = range * 0.15;

        return {
            min: min - bottomPad,
            max: max + topPad,
        };
    }, [seriesData]);

    const options = useMemo<Highcharts.Options>(() => {
        const defaultTooltipFormatter = function (this: TooltipFormatterContext) {
            const value = typeof this.y === "number" ? FORMATTERS.percentSign(this.y, DEFAULT_DECIMAL) : this.y || null;
            const locale = (swSettings.components?.Home?.resources?.Language as string) ?? "en-US";
            const date = dayjs(this.key).locale(locale.slice(0, 2)).format("MMMM DD, YYYY");

            return ReactDOMServer.renderToString(
                <SC.TooltipContainer>
                    <span>{date}</span>
                    <span>{value}</span>
                </SC.TooltipContainer>,
            );
        };
        const tooltip: Highcharts.TooltipOptions = tooltipEnabled
            ? {
                  enabled: true,
                  shared: false,
                  useHTML: true,
                  followPointer: true,
                  backgroundColor: colors.tooltipBg,
                  borderColor: colors.tooltipBorder,
                  style: { color: colors.tooltipText as any },
                  formatter: (tooltipFormatter ?? defaultTooltipFormatter) as any,
                  ...tooltipOptions,
              }
            : { enabled: false };
        const fillColor: Highcharts.ColorType | undefined = gradient
            ? {
                  linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
                  stops: [
                      [0, gradient.startColor],
                      [1, gradient.endColor],
                  ],
              }
            : undefined;

        const chartType = gradient ? "area" : "line";

        const base: Highcharts.Options = {
            chart: {
                type: chartType,
                height,
                width: null,
                backgroundColor: colors.background,
                spacing: [0, 0, 0, 0],

                animation: false,
            },
            title: { text: title ?? undefined },
            credits: { enabled: false },
            legend: { enabled: false },
            xAxis: {
                visible: false,
                categories,
            },
            yAxis: {
                visible: false,
                startOnTick: false,
                endOnTick: false,
                minPadding: 0,
                maxPadding: 0,
                ...(yExtremes ? { min: yExtremes.min, max: yExtremes.max } : {}),
            },
            plotOptions: {
                series: {
                    animation: false,
                    marker: {
                        enabled: hasMarkers,
                        radius: 3,
                        fillColor: colors.markerColor,
                        lineColor: colors.markerColor,
                    },
                    lineWidth: lineWidth,
                    linecap: "square",
                    states: { hover: { enabled: hasHover } },
                },
                areaspline: {
                    fillOpacity: colors.areaEnabled ? 1 : 0,
                },
            },
            exporting: {
                enabled: false,
            },
            tooltip,
            series: [{ type: chartType, data: seriesData as any, color: colors.lineColor, lineWidth, fillColor }],
        };
        return deepMerge(base, configOverride ?? {});
    }, [
        categories,
        seriesData,
        title,
        tooltipEnabled,
        tooltipFormatter,
        tooltipOptions,
        configOverride,
        colors,
        hasHover,
        gradient,
        hasMarkers,
        lineWidth,
        height,
        yExtremes,
    ]);
    return (
        <SC.ChartWrapper>
            <ReactHighcharts
                allowChartUpdate
                options={options as any}
            />
        </SC.ChartWrapper>
    );
});
